'use client'

import React, { useState } from 'react'
import { Form, Formik } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import Link from 'next/link'
import { DialogModal } from '@/shared/components/DialogModal'
import { translateAuthenticationError } from '@/lib/utils'
import { loginWithPostHogSession } from '@/lib/apis/authentication-client'
import { useSession } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import clsx from 'clsx'
import Spinner from '@/shared/components/Spinner'
import SocialProviderChooser from '@/features/auth/SocialProviderChooser'
import useSocialAuthentication from '@/features/auth/useSocialAuthentication'
import { socialAuthenticationErrorMessage } from '@/lib/auth/social-auth'
import { currentRelativePath } from '@/lib/auth/social-navigation'

type Props = {
  open: boolean
  onClose: () => void
  onAuthenticated?: () => Promise<void>
  socialLoginMode?: 'popup' | 'same-tab'
  initialEmail?: string
  initialMessage?: string
}

export default function LoginModal({
  open,
  onClose,
  onAuthenticated,
  socialLoginMode = 'popup',
  initialEmail = '',
  initialMessage = '',
}: Props) {
  const [message, setMessage] = useState('')
  const { update } = useSession()
  const router = useRouter()
  const socialAuthentication = useSocialAuthentication({
    onAuthenticated: async () => {
      if (onAuthenticated) {
        await onAuthenticated()
      } else {
        router.refresh()
        onClose()
      }
    },
  })
  const displayedMessage =
    message ||
    (socialAuthentication.error
      ? socialAuthenticationErrorMessage(socialAuthentication.error)
      : '') ||
    initialMessage

  function startSocialAuthentication(
    provider: Parameters<typeof socialAuthentication.start>[0],
  ) {
    if (socialLoginMode === 'popup') {
      return socialAuthentication.start(provider, { popup: true })
    }

    const returnTo = currentRelativePath(window.location)
    return socialAuthentication.start(provider, {
      returnTo,
      errorTo: returnTo,
    })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{ username: initialEmail, password: '' }}
      onSubmit={async (values, { setSubmitting }) => {
        setMessage('')

        const formData = new FormData()
        formData.append('username', values.username)
        formData.append('password', values.password)
        formData.append('redirect', 'false')

        try {
          const err = await loginWithPostHogSession(formData)
          if (err) {
            setMessage(translateAuthenticationError(err))
            return
          }

          if (onAuthenticated) {
            await onAuthenticated()
            return
          }

          await update()
          router.refresh()
          onClose()
        } finally {
          setSubmitting(false)
        }
      }}
    >
      {({ isSubmitting, submitForm, values, handleChange }) => (
        <DialogModal
          show={open}
          onClose={() => {
            if (!isSubmitting) onClose()
          }}
          motionPreset="editorial"
          panelClasses="!max-w-[440px] !rounded-[24px] !p-0"
          panelContainerClasses="!items-center p-5"
        >
          <div className="flex flex-col gap-[18px] p-6 text-[#0B0B0F] sm:p-8">
            <div className="flex items-start gap-5">
              <h1 className="text-[22px] font-bold leading-7 tracking-[-0.01em]">
                Welcome back.
              </h1>
              <button
                aria-label="Close sign in"
                className="ml-auto mt-1 flex h-5 w-5 items-center justify-center text-[#A0A0A8] transition-colors duration-300 hover:text-[#5C5C64] disabled:cursor-not-allowed"
                disabled={isSubmitting}
                onClick={onClose}
                type="button"
              >
                <CloseIcon />
              </button>
            </div>

            <p className="text-[14px] leading-5 text-[#5C5C64]">
              Sign in to reuse your saved Medicare details.
            </p>

            <Form className="flex flex-col gap-[18px]">
              <QuizLoginInput
                autoComplete="email"
                disabled={isSubmitting}
                hasError={Boolean(displayedMessage)}
                label="Email"
                name="username"
                onChange={handleChange}
                type="email"
                value={values.username}
              />
              <QuizLoginInput
                autoComplete="current-password"
                disabled={isSubmitting}
                hasError={Boolean(displayedMessage)}
                label="Password"
                name="password"
                onChange={handleChange}
                type="password"
                value={values.password}
              />

              {displayedMessage && (
                <p className="-mt-2 flex items-start gap-2 text-[13px] font-normal leading-[18px] text-[#C6453C]">
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0"
                  />
                  {displayedMessage}
                </p>
              )}

              <button
                className="mt-1 flex h-[50px] w-full items-center justify-center gap-2 rounded-full bg-[#0B0B0F] px-6 text-[15px] font-semibold leading-[19px] text-white transition-[background-color,opacity] duration-300 hover:bg-[#242428] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSubmitting}
                onClick={() => void submitForm()}
                type="button"
              >
                {isSubmitting ? <Spinner /> : 'Sign in and continue →'}
              </button>

              <Link
                href="/forgot-password"
                className="-mt-1 text-center text-[13.5px] font-semibold leading-[18px] text-[#2563EB] transition-colors duration-300 hover:text-[#1D4ED8]"
              >
                Forgotten your password?
              </Link>

              <SocialProviderChooser
                action="sign-in"
                disabled={isSubmitting}
                onSelect={startSocialAuthentication}
                pendingProvider={socialAuthentication.pendingProvider}
                variant="quiz"
              />
            </Form>
          </div>
        </DialogModal>
      )}
    </Formik>
  )
}

function QuizLoginInput({
  autoComplete,
  disabled,
  hasError,
  label,
  name,
  onChange,
  type,
  value,
}: {
  autoComplete: string
  disabled: boolean
  hasError: boolean
  label: string
  name: string
  onChange: React.ChangeEventHandler<HTMLInputElement>
  type: React.HTMLInputTypeAttribute
  value: string
}) {
  return (
    <label className="flex flex-col gap-2 text-[13.5px] font-semibold leading-[18px]">
      {label}
      <input
        autoComplete={autoComplete}
        className={clsx(
          'h-[52px] rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-[18px] text-[15px] font-normal leading-5 text-[#0B0B0F] outline-none transition-colors duration-300 focus:border-[#0B0B0F]/45 disabled:cursor-not-allowed disabled:opacity-60',
          hasError && 'border-[#C6453C] focus:border-[#C6453C]',
        )}
        disabled={disabled}
        name={name}
        onChange={onChange}
        type={type}
        value={value}
      />
    </label>
  )
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path
        d="M18 6 6 18M6 6l12 12"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  )
}
